import React, { useState, useEffect } from 'react';
import {
  TextInput,
  Select,
  Stack,
  Group,
  Button,
  Textarea,
  Checkbox,
  Text,
  Grid,
  Input,
  Popover,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useClient } from '../../context/ClientContext';
import { formatPhoneNumber, validatePhoneNumber, splitPhoneNumber } from '../../utils/phoneUtils';
import { IconCalendar } from '@tabler/icons-react';

const genderOptions = [
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
  { value: 'Non-binary', label: 'Non-binary' },
  { value: 'Other', label: 'Other' },
  { value: 'Prefer not to say', label: 'Prefer not to say' }
];

const pronounOptions = [
  { value: 'He/Him', label: 'He/Him' },
  { value: 'She/Her', label: 'She/Her' },
  { value: 'They/Them', label: 'They/Them' },
  { value: 'Other', label: 'Other' }
];

function ClientForm({ client, onClose }) {
  const { createClient, updateClient } = useClient();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    gender: '',
    pronouns: '',
    client_notes: '',
    is_opted_in_reminder: false,
    is_opted_in_marketing: false,
    birthday: null
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (client) {
      // Combine area_code and phone_number into a single phone field
      const fullPhone = client.area_code && client.phone_number 
        ? `${client.area_code}${client.phone_number}`
        : '';
        
      setFormData({
        first_name: client.first_name || '',
        last_name: client.last_name || '',
        phone: fullPhone ? formatPhoneNumber(fullPhone) : '',
        email: client.email || '',
        gender: client.gender || '',
        pronouns: client.pronouns || '',
        client_notes: client.client_notes || '',
        is_opted_in_reminder: client.is_opted_in_reminder || false,
        is_opted_in_marketing: client.is_opted_in_marketing || false,
        birthday: client.birthday ? new Date(client.birthday) : null
      });
    }
  }, [client]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }
    
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhoneNumber(formData.phone)) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }
    
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Validate birthday is not in the future
    if (formData.birthday && formData.birthday > new Date()) {
      newErrors.birthday = 'Birthday cannot be in the future';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const currentFormData = { ...formData };
    
    if (!validateForm()) {
      return;
    }

    try {
      // Split the phone number into area_code and phone_number
      const { areaCode, phoneNumber } = splitPhoneNumber(currentFormData.phone);
      
      const clientData = {
        ...currentFormData,
        area_code: areaCode,
        phone_number: phoneNumber
      };
      
      // Remove the combined phone field before sending to API
      delete clientData.phone;

      if (client) {
        await updateClient(client.id, clientData);
      } else {
        await createClient(clientData);
      }
      onClose();
    } catch (error) {
      console.error('Error saving client:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing="md">
        <Grid>
          <Grid.Col span={6}>
            <TextInput
              label="First Name"
              required
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              error={errors.first_name}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label="Last Name"
              required
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              error={errors.last_name}
            />
          </Grid.Col>
        </Grid>

        <TextInput
          label="Phone"
          required
          placeholder="(xxx) xxx-xxxx"
          value={formData.phone}
          onChange={(e) => {
            const formatted = formatPhoneNumber(e.target.value);
            setFormData({ ...formData, phone: formatted });
            // Clear error if it exists
            if (errors.phone) {
              setErrors({ ...errors, phone: '' });
            }
          }}
          error={errors.phone}
          onBlur={() => {
            if (formData.phone && !validatePhoneNumber(formData.phone)) {
              setErrors({ ...errors, phone: 'Please enter a valid 10-digit phone number' });
            }
          }}
        />

        <TextInput
          label="Email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          error={errors.email}
        />

        <Grid>
          <Grid.Col span={6}>
            <Select
              label="Gender"
              data={genderOptions}
              value={formData.gender}
              onChange={(value) => setFormData({ ...formData, gender: value })}
              clearable
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <Select
              label="Pronouns"
              data={pronounOptions}
              value={formData.pronouns}
              onChange={(value) => setFormData({ ...formData, pronouns: value })}
              clearable
            />
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={6}>
            <DateInput
              label="Birthday"
              placeholder="mm/dd/yyyy"
              value={formData.birthday}
              onChange={(date) => setFormData({ ...formData, birthday: date })}
              maxDate={new Date()}
              clearable
              valueFormat="MM/DD/YYYY"
              error={errors.birthday}
            />
          </Grid.Col>
        </Grid>

        <Textarea
          label="Client Notes"
          value={formData.client_notes}
          onChange={(e) => setFormData({ ...formData, client_notes: e.target.value })}
          minRows={3}
        />

        <Stack spacing="xs">
          <Checkbox
            label="Opt-in to appointment-related text messages"
            checked={formData.is_opted_in_reminder}
            onChange={(e) => setFormData({ ...formData, is_opted_in_reminder: e.target.checked })}
          />
          <Text size="xs" c="dimmed">
            Please read the customer the following message:
          </Text>
          <Text size="xs" c="dimmed">
            "Would you like to receive informational text messages about your appointment from Pagoda Labs? There will be about 3 text messages per appointment and will not include marketing content.
          </Text>
          <Text size="xs" c="dimmed">
            You can view their privacy policy and terms & conditions on their website, www.getpagoda.com. You can always reply HELP for more information or STOP to opt-out of text messages.
          </Text>
          <Text size="xs" c="dimmed">
            Message & data rates may apply."
          </Text>
        </Stack>

        <Checkbox
          label="Opt-in to marketing communications"
          checked={formData.is_opted_in_marketing}
          onChange={(e) => setFormData({ ...formData, is_opted_in_marketing: e.target.checked })}
        />

        <Group position="right" mt="md">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" color="violet">
            {client ? 'Update Client' : 'Create Client'}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

export default ClientForm; 